import React from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, Shield } from 'lucide-react';
import { Link } from 'react-router-dom';

const PrivacyPolicy = () => {
  // Check for utm=mobile query parameter
  const urlParams = new URLSearchParams(window.location.search);
  const isMobile = urlParams.get('utm') === 'mobile';
  const privacyContent = `
**Effective Date:** 05/Oct/2025  
**Last Updated:** 13/Jan/2026  

## 1. Introduction  
Resiara Software Pvt. Ltd. ("we", "us", "our") operates the website [https://metrorahi.resiara.com](https://metrorahi.resiara.com) ("Website").  
This Privacy Policy describes how we collect, use, and protect your information when you visit our Website.  

By accessing or using the Website, you consent to data practices described in this policy.

---

## 2. Government Information Disclaimer

MetroRahi is an independent, privately developed application and is not affiliated with, endorsed by, or representing any government entity or metro authority.

The app does not provide official government services and does not claim authorization from any metro organization.

Metro-related information displayed in the app is collected from publicly available official sources such as metro authority websites and is provided for informational purposes only.

For official announcements, fare updates, service changes, or operational notices, users should always refer to the respective metro authority's official website.

To improve accessibility, the app may present information in multiple regional languages. Any translations provided are intended to enhance understanding and do not represent official communication from any metro authority.

Metro-related information in the app may reference publicly available data from official metro authority websites, including but not limited to:

- Delhi Metro Rail Corporation (DMRC): https://www.delhimetrorail.com
- Mumbai Metro Rail Corporation (MMRCL): https://mmrcl.com/en
- Jaipur Metro: https://transport.rajasthan.gov.in/content/transportportal/en/metro.html
- Pune Metro Rail Project: https://www.punemetrorail.org
- Chennai Metro Rail Limited: https://chennaimetrorail.org
- Hyderabad Metro Rail: https://ltmetro.com and https://hmrl.co.in
- Bangalore Metro Rail Corporation Limited (BMRCL): https://english.bmrc.co.in

These links are provided for transparency and reference only.

---

## 3. Information We Collect  

### 3.1 Automatically Collected Data  
When you visit, we may collect:  
- IP address  
- Browser type & version  
- Device type and operating system  
- Referring / exit pages  
- Date/time of visit  
- Pages viewed  
- Analytics & usage data  

### 3.2 Cookies & Tracking Technologies  
We use cookies and similar tools for:  
- Remembering preferences  
- Website performance  
- Analytics insights  
- Improving user experience  

You can manage cookies in your browser settings.

### 3.3 Contact / Inquiry Data  
If you contact us, we may collect:  
- Name  
- Email  
- Message details  

Used only to respond to your inquiry.

---

## 4. How We Use Information  
We use information to:  
- Improve and maintain the Website  
- Analyze visitor traffic  
- Respond to inquiries  
- Ensure compliance with laws  

---

## 5. Data Sharing & Disclosure  
We do **not** sell or rent personal data. We may share:  
- With service providers (hosting, analytics)  
- For legal compliance (court orders, authorities)  
- During mergers/acquisitions with data protections  

---

## 6. Data Security  
We implement reasonable security measures, but no system is 100% secure. Users acknowledge risks of online data transmission.

---

## 7. Data Retention  
- Analytics: up to **1 year**  
- Contact data: kept until resolved or user requests deletion  

---

## 8. Children  
Our Website is not for children under **13 years**. We do not knowingly collect their data.

---

## 9. Updates  
We may update this policy periodically. Continued use of the Website means acceptance of changes.

---

## 10. Contact Us  
For questions or data requests:  

📧 **support.metrorahi@resiara.com**  
🏢 **Resiara Software Pvt. Ltd., Jaipur, Rajasthan, India**

---

**By using MetroRahi, you acknowledge that you have read, understood, and agree to be bound by these Privacy Policy.**

**Last Updated:** 13/Jan/2026

**Version:** 1.0`;

  return (
    <div className="policy-page">
      <div className="policy-container">
        <div className="policy-document">
          {/* Header */}
          <motion.div
            className="policy-header"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            {!isMobile && (
              <Link to="/" className="back-button">
                <ArrowLeft className="back-icon" />
                <span>Back</span>
              </Link>
            )}
            
            <div className="policy-title-section">
              <h1>Privacy Policy</h1>
              <p>Website Privacy Policy</p>
            </div>
          </motion.div>

          {/* Content */}
          <motion.div
            className="policy-content"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            <div className="policy-text">
              {privacyContent.split('\n').map((line, index) => {
                if (line.startsWith('# ')) {
                  return <h1 key={index}>{line.substring(2)}</h1>;
                } else if (line.startsWith('## ')) {
                  return <h2 key={index}>{line.substring(3)}</h2>;
                } else if (line.startsWith('### ')) {
                  return <h3 key={index}>{line.substring(4)}</h3>;
                } else if (line.startsWith('**') && line.endsWith('**')) {
                  return <p key={index} className="bold-text">{line.substring(2, line.length - 2)}</p>;
                } else if (line.startsWith('- ')) {
                  return <li key={index}>{line.substring(2)}</li>;
                } else if (line.trim() === '---') {
                  return <hr key={index} className="section-divider" />;
                } else if (line.trim() === '') {
                  return <br key={index} />;
                } else {
                  return <p key={index}>{line}</p>;
                }
              })}
            </div>
          </motion.div>

          {/* Footer */}
          <motion.div
            className="policy-footer"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <div className="company-info">
              <p>Brought to you by <a href="https://resiara.com" target="_blank" rel="noopener noreferrer" className="company-link">Resiara Software Pvt. Ltd.</a></p>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
